import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { Attribute, AttributeWithUsers } from 'services/bidAnalysis/bidAnalysisServices';
import classes from '../ReferenceTables.module.scss';

interface UserAttributeModalProps {
  // Add Users Modal props
  addModalVisible: boolean;
  onAddModalHide: () => void;
  onSaveUsers: () => void;
  selectedAttributeForAdd: Attribute | null;
  onSelectAttributeForAdd: (attr: Attribute | null) => void;
  
  // Edit Users Modal props
  editModalVisible: boolean;
  onEditModalHide: () => void;
  selectedAttributeForUsers: AttributeWithUsers | null;
  
  // Common props
  selectedUserIds: string[];
  onSelectUserIds: (ids: string[]) => void;
  attributes: Attribute[];
  usersList: any[];
  isLoadingUsersList: boolean;
  savingUsers: boolean;
}

export const UserAttributeModal = ({
  addModalVisible,
  onAddModalHide,
  onSaveUsers,
  selectedAttributeForAdd,
  onSelectAttributeForAdd,
  editModalVisible,
  onEditModalHide,
  selectedAttributeForUsers,
  selectedUserIds,
  onSelectUserIds,
  attributes,
  usersList,
  isLoadingUsersList,
  savingUsers,
}: UserAttributeModalProps) => {
  return (
    <>
      {/* Add Users Modal */}
      <Dialog
        header="Add Users to Attribute"
        visible={addModalVisible}
        style={{ width: '600px' }}
        onHide={onAddModalHide}
        footer={
          <div>
            <Button
              label="Cancel"
              icon="pi pi-times"
              onClick={onAddModalHide}
              className="p-button-text"
            />
            <Button
              label="Add Users"
              icon="pi pi-check"
              onClick={onSaveUsers}
              className={classes.primaryButton}
              disabled={savingUsers}
              loading={savingUsers}
            />
          </div>
        }
      >
        <div className={classes.formContent}>
          <div className={classes.formField}>
            <label htmlFor="attribute_select">Attribute *</label>
            <Dropdown
              id="attribute_select"
              value={selectedAttributeForAdd}
              options={attributes}
              onChange={e => onSelectAttributeForAdd(e.value)}
              optionLabel="attribute_name"
              placeholder="Select an attribute"
              className={classes.inputField}
              filter
            />
          </div>
          <div className={classes.formField}>
            <label htmlFor="user_select">Users *</label>
            {isLoadingUsersList ? (
              <div className={classes.loadingContainer}>
                <i
                  className="pi pi-spinner pi-spin"
                  style={{ fontSize: '1.5rem', color: '#522d6e' }}
                ></i>
                <p>Loading users...</p>
              </div>
            ) : (
              <MultiSelect
                id="user_select"
                value={selectedUserIds}
                options={usersList.map((user: any) => ({
                  label: user.name ? `${user.name} (${user.email})` : user.email,
                  value: user.email,
                }))}
                onChange={e => onSelectUserIds(e.value || [])}
                placeholder="Select users"
                className={`${classes.inputField} ${classes.multiSelectWide}`}
                filter
                disabled={savingUsers}
                display="chip"
                maxSelectedLabels={2}
                selectedItemsLabel="{0} users selected"
                showSelectAll={false}
              />
            )}
          </div>
        </div>
      </Dialog>

      {/* Edit Users Modal */}
      <Dialog
        header={`Edit Users for ${selectedAttributeForUsers?.attribute_name || 'Attribute'}`}
        visible={editModalVisible}
        style={{ width: '600px' }}
        onHide={onEditModalHide}
        footer={
          <div>
            <Button
              label="Cancel"
              icon="pi pi-times"
              onClick={onEditModalHide}
              className="p-button-text"
            />
            <Button
              label="Update Users"
              icon="pi pi-check"
              onClick={onSaveUsers}
              className={classes.primaryButton}
              disabled={savingUsers}
              loading={savingUsers}
            />
          </div>
        }
      >
        <div className={classes.formContent}>
          <div className={classes.formField}>
            <label htmlFor="edit_user_select">Select Users *</label>
            {isLoadingUsersList ? (
              <div className={classes.loadingContainer}>
                <i
                  className="pi pi-spinner pi-spin"
                  style={{ fontSize: '1.5rem', color: '#522d6e' }}
                ></i>
                <p>Loading users...</p>
              </div>
            ) : (
              <>
                <MultiSelect
                  id="edit_user_select"
                  value={selectedUserIds}
                  options={usersList.map((user: any) => ({
                    label: user.name ? `${user.name} (${user.email})` : user.email,
                    value: user.email,
                  }))}
                  onChange={e => onSelectUserIds(e.value || [])}
                  placeholder="Select users"
                  className={`${classes.inputField} ${classes.multiSelectWide}`}
                  filter
                  disabled={savingUsers}
                  display="chip"
                  maxSelectedLabels={2}
                  selectedItemsLabel="{0} users selected"
                  showSelectAll={false}
                />
                <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#6b7280' }}>
                  {selectedUserIds.length} user{selectedUserIds.length !== 1 ? 's' : ''} selected
                </div>
              </>
            )}
          </div>
        </div>
      </Dialog>
    </>
  );
};

